import { useEffect, useState } from "react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { apiGet, fmt } from "@/lib/api";

const RANGES = [["today", "Hari Ini"], ["7d", "7 Hari"], ["30d", "30 Hari"], ["month", "Bulan Ini"], ["all", "Semua"]];

const M = ({ label, value, cls = "text-white", testid }) => (
  <div className="border border-white/10 bg-[#0d1219] px-3.5 py-3" data-testid={testid}>
    <div className="font-mono text-[8px] uppercase tracking-widest text-mute-dim">{label}</div>
    <div className={`mt-1 font-mono text-base font-bold ${cls}`}>{value}</div>
  </div>
);

export const PnlPage = () => {
  const [range, setRange] = useState("all");
  const [sym, setSym] = useState("all");
  const [data, setData] = useState(null);
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [customApplied, setCustomApplied] = useState(false);

  useEffect(() => {
    setData(null);

    const params = new URLSearchParams();

    if (customApplied && (customStart || customEnd)) {
      if (customStart) params.set("start_date", customStart);
      if (customEnd) params.set("end_date", customEnd);
    } else {
      params.set("range", range);
    }

    if (sym !== "all") params.set("symbol", sym);

    apiGet(`/pnl?${params.toString()}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [range, sym, customStart, customEnd, customApplied]);

  const m = data?.metrics;
  const pnlCls = (v) => (v >= 0 ? "text-buy" : "text-sell");

  return (
    <div data-testid="pnl-page">
      <div className="mb-4 flex flex-wrap items-center gap-1.5">
        {RANGES.map(([key, label]) => (
          <button key={key} onClick={() => { setRange(key); setCustomApplied(false); }} data-testid={`pnl-range-${key}`}
            className={`border px-3 py-1.5 font-mono text-[10px] font-bold uppercase transition-colors ${
              range === key ? "border-buy bg-buy/15 text-buy" : "border-white/15 text-mute hover:text-white"}`}>
            {label}
          </button>
        ))}
        <span className="mx-1 hidden h-5 w-px bg-white/15 sm:inline" />
        <div className="mt-3 w-full border border-white/10 bg-[#0d1219] p-3">
          <div className="mb-2 font-mono text-[9px] uppercase tracking-[0.2em] text-mute-dim">
            Custom Date
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <label className="font-mono text-[9px] text-mute">
              <span className="mb-1 block">Tanggal Mulai</span>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="border border-white/15 bg-[#080b10] px-2 py-1.5 font-mono text-xs text-white"
                data-testid="pnl-custom-start"
              />
            </label>

            <label className="font-mono text-[9px] text-mute">
              <span className="mb-1 block">Tanggal Akhir</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="border border-white/15 bg-[#080b10] px-2 py-1.5 font-mono text-xs text-white"
                data-testid="pnl-custom-end"
              />
            </label>

            <button
              onClick={() => {
                if (!customStart && !customEnd) return;
                if (customStart && customEnd && customStart > customEnd) return;
                setCustomApplied(true);
              }}
              disabled={!customStart && !customEnd}
              className="border border-buy bg-buy/15 px-3 py-1.5 font-mono text-[10px] font-bold uppercase text-buy disabled:cursor-not-allowed disabled:opacity-40"
              data-testid="pnl-custom-apply"
            >
              Terapkan
            </button>

            {customApplied && (
              <button
                onClick={() => {
                  setCustomStart("");
                  setCustomEnd("");
                  setCustomApplied(false);
                  setRange("all");
                }}
                className="border border-white/15 px-3 py-1.5 font-mono text-[10px] font-bold uppercase text-mute hover:text-white"
                data-testid="pnl-custom-reset"
              >
                Reset
              </button>
            )}
          </div>
        </div>

        <button onClick={() => setSym("all")} data-testid="pnl-symbol-all"
          className={`border px-3 py-1.5 font-mono text-[10px] font-bold uppercase transition-colors ${
            sym === "all" ? "border-brand bg-brand/15 text-[#8b97f5]" : "border-white/15 text-mute hover:text-white"}`}>
          Semua Pair
        </button>
        {["XAUUSD", "EURUSD", "GBPUSD", "USDJPY", "BTCUSD"].map((s) => (
          <button key={s} onClick={() => setSym(s)} data-testid={`pnl-symbol-${s.toLowerCase()}`}
            className={`border px-3 py-1.5 font-mono text-[10px] font-bold transition-colors ${
              sym === s ? "border-brand bg-brand/15 text-[#8b97f5]" : "border-white/15 text-mute hover:text-white"}`}>
            {s}
          </button>
        ))}
      </div>

      {!data ? (
        <p className="font-mono text-xs text-mute-dim">memuat statistik...</p>
      ) : (
        <>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-6">
            <M label="Total Pips" value={`${m.total_pnl >= 0 ? "+" : ""}${fmt(m.total_pnl)} pips`} cls={pnlCls(m.total_pnl)} testid="pnl-total" />
            <M label="Total Trades" value={m.trades} />
            <M label="Win Rate" value={`${m.win_rate}%`} cls={m.win_rate >= 50 ? "text-buy" : "text-warn"} testid="pnl-winrate" />
            <M label="Profit Factor" value={m.profit_factor} cls={m.profit_factor >= 1 ? "text-buy" : "text-sell"} />
            <M label="Menang / Kalah" value={`${m.wins} / ${m.losses}`} />
            <M label="Avg Win / Loss" value={`${fmt(m.avg_win)} / ${fmt(m.avg_loss)} pips`} />
            <M label="Best Trade" value={`+${fmt(m.best_trade)} pips`} cls="text-buy" />
            <M label="Worst Trade" value={`${fmt(m.worst_trade)} pips`} cls="text-sell" />
            <M label="Avg Risk/Reward" value={m.avg_rr || "—"} />
            <M label="Total / Avg Pips" value={`${m.total_pips} / ${m.avg_pips} pips`} />
            <M label="Max Drawdown" value={`${m.max_drawdown_pct}%`} cls="text-sell" />
            <M label="Win Rate Sinyal" value={data.signal_stats.total ? `${data.signal_stats.win_rate}% (${data.signal_stats.wins}/${data.signal_stats.total})` : "—"} testid="pnl-signal-winrate" />
          </div>

          <div className="mt-4 grid gap-3 lg:grid-cols-2">
            <div className="border border-white/10 bg-[#0d1219] p-4">
              <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">Equity Curve (paper)</p>
              {data.equity_curve.length === 0 ? (
                <p className="py-10 text-center font-mono text-xs text-mute-dim">belum ada trade tertutup pada periode ini</p>
              ) : (
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.equity_curve} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="pnlGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#00E676" stopOpacity={0.3} />
                          <stop offset="100%" stopColor="#00E676" stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="t" hide />
                      <YAxis domain={["auto", "auto"]} tick={{ fill: "#5C5C66", fontSize: 10, fontFamily: "JetBrains Mono" }} width={70} />
                      <Tooltip contentStyle={{ background: "#0C0C10", border: "1px solid rgba(255,255,255,0.15)", fontFamily: "JetBrains Mono", fontSize: 11 }} />
                      <Area type="monotone" dataKey="eq" stroke="#00E676" fill="url(#pnlGrad)" strokeWidth={1.5} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <div className="border border-white/10 bg-[#0d1219] p-4">
                <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">PnL per Simbol</p>
                {Object.keys(data.per_symbol).length === 0 ? (
                  <p className="font-mono text-xs text-mute-dim">belum ada data</p>
                ) : (
                  <div className="space-y-2 font-mono text-xs">
                    {Object.entries(data.per_symbol).map(([sym, s]) => (
                      <div key={sym} className="flex items-center justify-between" data-testid={`pnl-symbol-${sym.toLowerCase()}`}>
                        <span className="font-bold text-white">{sym} <span className="text-mute-dim">({s.trades}x, {s.wins}W)</span></span>
                        <span className={pnlCls(s.pnl)}>{s.pnl >= 0 ? "+" : ""}{fmt(s.pnl)} pips</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="border border-white/10 bg-[#0d1219] p-4">
                <p className="mb-3 font-mono text-[9px] uppercase tracking-[0.25em] text-mute-dim">PnL per Hari</p>
                {Object.keys(data.per_day).length === 0 ? (
                  <p className="font-mono text-xs text-mute-dim">belum ada data</p>
                ) : (
                  <div className="max-h-40 space-y-1.5 overflow-y-auto font-mono text-xs">
                    {Object.entries(data.per_day).reverse().map(([d, v]) => (
                      <div key={d} className="flex justify-between">
                        <span className="text-mute">{d}</span>
                        <span className={pnlCls(v)}>{v >= 0 ? "+" : ""}{fmt(v)} pips</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};
